import React from 'react'
import {Box, Button, Card, Link, Skeleton, Stack, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import SensorsOffRoundedIcon from '@mui/icons-material/SensorsOffRounded'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useGetMeterReadingDashboardQuery, useGetMetersDashboardQuery} from 'store/Store'
import {buildListLink, todayRange} from 'components/Page/Dashboard/dashboardFormat'

const SILENT_HOURS = 2

const AttentionItem = ({icon, color, title, helper, actionLabel, onAction}) => (
    <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
            flex: '1 1 280px',
            minWidth: 0,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(color, 0.08),
        }}>
        <Box sx={{color, display: 'grid', placeItems: 'center'}}>{icon}</Box>
        <Box sx={{flex: 1, minWidth: 0}}>
            <Typography variant="body2" fontWeight={700} noWrap>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
                {helper}
            </Typography>
        </Box>
        <Button size="small" onClick={onAction} sx={{textTransform: 'none', flexShrink: 0}}>
            {actionLabel}
        </Button>
    </Stack>
)

const AttentionPanel = ({category}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const navigate = useNavigate()
    const {data: readings, isLoading: readingsLoading} = useGetMeterReadingDashboardQuery({
        category,
    })
    const {data: meters, isLoading: metersLoading} = useGetMetersDashboardQuery({
        category,
        silentHours: SILENT_HOURS,
    })

    if (readingsLoading || metersLoading) {
        return <Skeleton variant="rounded" height={72} />
    }

    const failed = readings?.summary?.failed ?? 0
    const silentCount = meters?.silentCount ?? 0
    const totalActive = meters?.totalActive ?? 0
    const view = t('dashboard.attention.view')

    if (!failed && !silentCount) {
        return (
            <Card>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{p: 2}}>
                    <CheckCircleOutlineRoundedIcon sx={{color: palette.success.main}} />
                    <Typography variant="body2" fontWeight={700}>
                        {t('dashboard.attention.allGood')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('dashboard.attention.allGoodHelper', {
                            reporting: totalActive,
                            total: totalActive,
                        })}
                    </Typography>
                </Stack>
            </Card>
        )
    }

    // The meters list can't be filtered by id, so each silent meter links to a search for it.
    const silentLinks = (meters?.silent || []).slice(0, 3).map(({_id, name, meterId}) => (
        <Link
            key={_id}
            component="button"
            variant="caption"
            underline="hover"
            onClick={() => navigate(buildListLink('/meters', {search: meterId}))}>
            {name || meterId}
        </Link>
    ))

    return (
        <Card>
            <Stack sx={{p: 2}} spacing={1.5}>
                <Typography variant="subtitle2" fontWeight={700}>
                    {t('dashboard.attention.title')}
                </Typography>
                <Stack direction="row" gap={1.5} flexWrap="wrap">
                    {failed > 0 && (
                        <AttentionItem
                            icon={<ErrorOutlineRoundedIcon />}
                            color={palette.error.main}
                            title={t('dashboard.attention.failed', {count: failed})}
                            helper={t('dashboard.attention.failedHelper')}
                            actionLabel={view}
                            onAction={() =>
                                navigate(
                                    buildListLink('/meter_readings', {
                                        status: 'failed',
                                        ...todayRange(),
                                    })
                                )
                            }
                        />
                    )}
                    {silentCount > 0 && (
                        <AttentionItem
                            icon={<SensorsOffRoundedIcon />}
                            color={palette.warning.main}
                            title={t('dashboard.attention.silent', {count: silentCount})}
                            helper={
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    {silentLinks}
                                    <span>
                                        ·{' '}
                                        {t('dashboard.attention.silentHelper', {
                                            hours: SILENT_HOURS,
                                        })}
                                    </span>
                                </Stack>
                            }
                            actionLabel={view}
                            onAction={() =>
                                navigate(buildListLink('/meters', {category, active: 'true'}))
                            }
                        />
                    )}
                </Stack>
            </Stack>
        </Card>
    )
}

export default AttentionPanel
